import { fmtAge } from "@chula/shared";
import type { FallbackTier } from "@chula/shared";

/**
 * Plain-language explanation for each data tier — shown as the tooltip on
 * the tier badge and age chip so a first-time, non-technical viewer can
 * decode LIVE / CACHE / SCENARIO / REF / DB / OFFLINE without the manual.
 */
export const TIER_HINT: Record<FallbackTier, string> = {
  live: "real data from the source, updated now",
  database: "real data saved earlier in our own records — not fetched this second",
  cache: "last known data — the connection to the source is stale",
  scenario: "modeled estimate, not a measurement",
  reference: "published reference data — real, but only updated occasionally",
  unavailable: "the source is offline — nothing current to show",
};

/**
 * Text for the freshness chip next to a panel title, or null when no chip
 * should be shown. The one rule that matters: the word LIVE appears only for
 * the `live` tier.
 *
 * `fmtAge(0)` (packages/shared) renders "LIVE" for any age under a minute —
 * right for a live feed, a lie for a simulated one stamped `ageMinutes: 0`.
 *
 *  - null tier (still loading) / unavailable / reference → no chip
 *    (OFFLINE and REF badges already say it; reference data has no freshness)
 *  - scenario → the model's computed time if it has one, else no chip
 *    (the SCENARIO badge carries it)
 *  - cache / database under a minute → "<1m", never LIVE
 */
export function ageChipText(
  tier: FallbackTier | null,
  ageMinutes: number | null | undefined,
): string | null {
  if (tier == null || ageMinutes == null || ageMinutes < 0) return null;
  if (tier === "unavailable" || tier === "reference") return null;
  if (ageMinutes < 1) {
    if (tier === "live") return fmtAge(ageMinutes);
    if (tier === "scenario") return null;
    return "<1m";
  }
  return fmtAge(ageMinutes);
}


/**
 * Grey word tag per non-live tier (design review 2026-09): calm typography,
 * not colour. Live shows nothing extra. "MODEL" rather than "SCENARIO" —
 * shorter and plainer for the executive reader.
 */
export const TIER_TAG: Record<FallbackTier, string> = {
  live: "",
  database: "DB",
  cache: "CACHED",
  scenario: "MODEL",
  reference: "REF",
  unavailable: "OFFLINE",
};
